import React, { useState, useEffect } from 'react'
import { Component, LocaleCode } from '../types/component'

interface ProductCarouselProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
}

export function ProductCarousel({
  component,
  style,
}: ProductCarouselProps) {
  const config = component.config as any
  const [currentIndex, setCurrentIndex] = useState(0)

  // Always use desktop view for renderer (hardcoded to 3 slides)
  const slidesToShow = (config.slidesToShowDesktop as number) || 3
  const showArrows = config.showArrows !== false
  const showDots = config.showDots !== false
  const autoplay = config.autoplay || false
  const autoplayInterval = (config.autoplayInterval as number) || 3

  const products = component.bigcommerceProducts || []
  const maxIndex = Math.max(0, products.length - slidesToShow)

  // Autoplay functionality
  useEffect(() => {
    if (!autoplay || products.length === 0) return

    const timer = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= maxIndex) {
          return 0 // Loop back to start
        }
        return prev + 1
      })
    }, autoplayInterval * 1000)

    return () => clearInterval(timer)
  }, [autoplay, autoplayInterval, maxIndex, products.length])

  // Show empty state if no products
  if (products.length === 0) {
    return (
      <div
        style={{
          ...style,
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '48px 0',
            color: '#9ca3af',
          }}
        >
          <p>No products selected</p>
          <p style={{ fontSize: '12px', marginTop: '4px' }}>
            Add products in the properties panel
          </p>
        </div>
      </div>
    )
  }

  const handlePrev = () => {
    setCurrentIndex(Math.max(0, currentIndex - 1))
  }

  const handleNext = () => {
    setCurrentIndex(Math.min(maxIndex, currentIndex + 1))
  }

  const containerStyle: React.CSSProperties = {
    ...style,
    position: 'relative',
  }

  const overflowStyle: React.CSSProperties = {
    overflow: 'hidden',
  }

  const carouselTrackStyle: React.CSSProperties = {
    display: 'flex',
    transition: 'transform 0.3s ease-in-out',
    gap: '16px',
    transform: `translateX(-${currentIndex * (100 / slidesToShow)}%)`,
  }

  const productCardStyle: React.CSSProperties = {
    flexShrink: 0,
    width: `calc(${100 / slidesToShow}% - ${((slidesToShow - 1) * 16) / slidesToShow}px)`,
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
  }

  const imageContainerStyle: React.CSSProperties = {
    aspectRatio: '1',
    backgroundColor: '#f3f4f6',
    marginBottom: '12px',
    borderRadius: '4px',
    overflow: 'hidden',
  }

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  }

  const placeholderStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ca3af',
    fontSize: '12px',
  }

  const arrowButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    zIndex: 10,
    fontSize: '20px',
  }

  return (
    <div style={containerStyle}>
      <div style={overflowStyle}>
        <div style={carouselTrackStyle}>
          {products.map((product) => (
            <div key={product.productId} style={productCardStyle}>
              <div style={imageContainerStyle}>
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    style={imageStyle}
                  />
                ) : (
                  <div style={placeholderStyle}>No Image</div>
                )}
              </div>
              <p
                style={{
                  fontWeight: '600',
                  fontSize: '14px',
                  marginBottom: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {product.name}
              </p>
              <p
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                }}
              >
                ${product.price.toFixed(2)}
              </p>
              {config.buttonText && (
                <button
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    borderRadius: '4px',
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                >
                  {config.buttonText as string}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      {showArrows && products.length > slidesToShow && (
        <>
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            style={{
              ...arrowButtonStyle,
              left: '8px',
              opacity: currentIndex === 0 ? 0.5 : 1,
              cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (currentIndex !== 0) {
                e.currentTarget.style.backgroundColor = '#f3f4f6'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff'
            }}
          >
            ‹
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
            style={{
              ...arrowButtonStyle,
              right: '8px',
              opacity: currentIndex >= maxIndex ? 0.5 : 1,
              cursor: currentIndex >= maxIndex ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (currentIndex < maxIndex) {
                e.currentTarget.style.backgroundColor = '#f3f4f6'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff'
            }}
          >
            ›
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {showDots && maxIndex > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '16px',
          }}
        >
          {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor:
                  idx === currentIndex ? '#3b82f6' : 'rgba(156, 163, 175, 0.3)',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
